import * as i0 from '@angular/core';
import { inject, DestroyRef, ApplicationRef, ComponentFactoryResolver, Injector, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup, FormControl } from '@angular/forms';
import { Observable, debounceTime, tap, finalize, Subject, defer, share } from 'rxjs';
import { DomPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';

const useTakeUntilDestroyed = () => {
    const destroyRef = inject(DestroyRef);
    return () => takeUntilDestroyed(destroyRef);
};

// http://www.qmo-a.com/blog/?p=2904
function getIEVersion() {
    let rv = -1;
    if (navigator.appName === 'Microsoft Internet Explorer') {
        const ua = navigator.userAgent;
        const re = new RegExp('MSIE ([0-9]{1,}[.0-9]{0,})');
        if (re.exec(ua) != null) {
            rv = parseFloat(RegExp.$1);
        }
    }
    else if (navigator.appName === 'Netscape') {
        const ua = navigator.userAgent;
        const re = new RegExp('Trident/.*rv:([0-9]{1,}[.0-9]{0,})'); // for IE 11
        if (re.exec(ua) != null) {
            rv = parseFloat(RegExp.$1);
        }
    }
    return rv;
}

function getScrollbarWidth() {
    /**
     * Browser Scrollbar Widths (2016)
     * OSX (Chrome, Safari, Firefox) - 15px
     * Windows XP (IE7, Chrome, Firefox) - 17px
     * Windows 7 (IE10, IE11, Chrome, Firefox) - 17px
     * Windows 8.1 (IE11, Chrome, Firefox) - 17px
     * Windows 10 (IE11, Chrome, Firefox) - 17px
     * Windows 10 (Edge 12/13) - 12px
     */
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);
    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = 'scroll';
    // add inner div
    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);
    const widthWithScroll = inner.offsetWidth;
    // remove div
    outer.parentNode.removeChild(outer);
    /**
     * Scrollbar width will be 0 on Mac OS with the
     * default "Only show scrollbars when scrolling" setting (Yosemite and up).
     * setting default with to 20;
     */
    return widthNoScroll - widthWithScroll || 20;
}

class ElementHandler {
    _renderer;
    _document;
    constructor(_renderer, _document) {
        this._renderer = _renderer;
        this._document = _document;
    }
    /**
     * add multiple style sheet once
     * @param elm target element
     * @param style style object
     */
    addStyles(elm, style) {
        if (style) {
            Object.keys(style).forEach((key) => {
                const value = style[key];
                this._renderer.setStyle(elm, key, value);
            });
        }
    }
    /**
     * you can add multiple class once
     * @param elm target element
     * @param className className string, work with multiple class like `class1 class2`
     */
    addClassByString(elm, className) {
        const currentClass = elm.getAttribute('class') || '';
        this._renderer.setAttribute(elm, 'class', [currentClass, className].join(' ').trim());
    }
    insertAfter(newEl, targetEl) {
        const parentEl = targetEl.parentNode;
        if (!parentEl)
            return;
        if (parentEl.lastChild === targetEl) {
            this._renderer.appendChild(parentEl, newEl);
        }
        else {
            this._renderer.insertBefore(parentEl, newEl, targetEl.nextSibling);
        }
    }
    getElmOffset(el) {
        const rect = el.getBoundingClientRect(), scrollLeft = window.pageXOffset || document.documentElement.scrollLeft, scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
    }
    removeElement(elm) {
        this._renderer.removeChild(elm.parentNode, elm);
    }
    elementsFromPoint(x, y, checkCb = () => true) {
        return new Promise((resolve) => {
            const checkedElm = [], elmsStyle = [];
            let elm, i, d;
            let getElm = null;
            // get all elements via elementFromPoint, and remove them from hit-testing in order
            while ((elm = this._document?.elementFromPoint(x, y)) &&
                checkedElm.indexOf(elm) === -1 &&
                elm != null) {
                // push the element and its current style
                checkedElm.push(elm);
                if (checkCb(elm)) {
                    getElm = elm;
                    break;
                }
                elmsStyle.push({
                    value: elm.style.getPropertyValue('pointer-events'),
                    priority: elm.style.getPropertyPriority('pointer-events'),
                });
                // set pointer-event be none, make that can find element to next layer
                elm.style.setProperty('pointer-events', 'none', 'important');
            }
            // restore the previous pointer-events values
            for (i = elmsStyle.length; (d = elmsStyle[--i]);) {
                checkedElm[i].style.setProperty('pointer-events', d.value ? d.value : '', d.priority);
            }
            if (getElm) {
                resolve(getElm);
            }
            else {
                resolve(null);
            }
        });
    }
}

const markControlDirty = (formControl) => {
    formControl.markAsDirty();
};
const markArrayDirty = (formArray) => {
    formArray.controls.forEach((control) => {
        checkControl(control);
    });
};
const markGroupDirty = (formGroup) => {
    Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control) {
            checkControl(control);
        }
    });
};
const markFormDirty = (form) => {
    if (form instanceof FormArray) {
        markArrayDirty(form);
        return;
    }
    markGroupDirty(form);
};
const markFormGroupDirtyByKey = (formGroup, keys) => {
    keys.forEach((key) => {
        const control = formGroup.get(key);
        if (control) {
            if (control instanceof FormArray) {
                markArrayDirty(control);
            }
            else {
                markControlDirty(control);
            }
        }
    });
};
function checkControl(control) {
    if (control instanceof FormGroup) {
        markGroupDirty(control);
    }
    else if (control instanceof FormArray) {
        markArrayDirty(control);
    }
    else if (control instanceof FormControl) {
        markControlDirty(control);
    }
}

const getFormGroupDirtyValues = (form) => {
    const dirtyValues = {};
    Object.keys(form.controls).forEach((key) => {
        const currentControl = form.controls[key];
        if (currentControl.dirty) {
            if (currentControl.controls) {
                dirtyValues[key] = getFormGroupDirtyValues(currentControl);
            }
            else {
                dirtyValues[key] = currentControl.value;
            }
        }
    });
    return dirtyValues;
};
const patchFormGroupFromDirtyValues = ({ form, dirtyValues, creatorMap = {}, overrideMap = {}, markDirty = true, }) => {
    Object.entries(dirtyValues).forEach(([key, value]) => {
        const currentControl = form.get(key);
        if (currentControl) {
            if (currentControl instanceof FormArray) {
                if (overrideMap[key]) {
                    currentControl.clear();
                }
                Object.entries(value).forEach(([k, v]) => {
                    let currCtrl = currentControl.at(+k);
                    if (!currCtrl) {
                        const creator = creatorMap[key] || creatorMap['default'];
                        const item = creator(v);
                        if (markDirty) {
                            markFormDirty(item);
                        }
                        currentControl.push(item);
                        currCtrl = item;
                    }
                    currCtrl.patchValue(v);
                    if (markDirty) {
                        currCtrl.markAsDirty();
                    }
                });
            }
            else if (currentControl instanceof FormGroup) {
                patchFormGroupFromDirtyValues({
                    form: currentControl,
                    dirtyValues: value,
                    creatorMap,
                });
            }
            else {
                currentControl.patchValue(value);
                if (markDirty) {
                    currentControl.markAsDirty();
                }
            }
        }
    });
    form.updateValueAndValidity();
    return dirtyValues;
};

/**
 * use for check current invalid controls
 */
const getInvalidControls = (formGroup) => {
    // get all invalid controls
    const invalidControls = formGroup.controls;
    const invalidControlsKeys = Object.keys(invalidControls);
    const invalidControlsValues = invalidControlsKeys.map((key) => [key, invalidControls[key]]);
    const invalidControlsValuesFiltered = invalidControlsValues.filter(([key, control]) => control.invalid);
    console.log(invalidControlsValuesFiltered);
};
/**
// * use in your debug tool
const getInvalidControls = function (formGroup) {
  // get all invalid controls
  var invalidControls = formGroup.controls;
  var invalidControlsKeys = Object.keys(invalidControls);
  var invalidControlsValues = invalidControlsKeys.map(function (key) { return [key, invalidControls[key]]; });
  var invalidControlsValuesFiltered = invalidControlsValues.filter(function (_a) {
      var key = _a[0], control = _a[1];
      return control.invalid;
  });
  console.log(invalidControlsValuesFiltered);
};
*/

/**
 * An observable creator for element resize.
 * @param elm the watch element.
 * @param cb when resize complete, call back function.
 * @param time resize emit time, default is 200
 */
function resizeObservable(elm, cb, time = 200) {
    let elmObserve$;
    return new Observable((observer) => {
        elmObserve$ = new ResizeObserver((entries, obs) => {
            observer.next(elmObserve$);
        });
        elmObserve$.observe(elm);
    }).pipe(debounceTime(time), tap(() => {
        cb();
    }), finalize(() => {
        elmObserve$.unobserve(elm);
        elmObserve$.disconnect();
    }));
}

class CdkService {
    document = inject(DOCUMENT);
    _appRef = inject(ApplicationRef);
    _componentFactoryResolver = inject(ComponentFactoryResolver);
    _injector = inject(Injector);
    createBodyPortalHost() {
        // TODO: wait material-cdk to fix this
        return new DomPortalOutlet(this.document.body, this._componentFactoryResolver, this._appRef, this._injector);
    }
    createInjector(key, data) {
        return Injector.create({
            providers: [
                {
                    provide: key,
                    useValue: data,
                },
            ],
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CdkService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CdkService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CdkService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * subscribe to get post worker messages,
 *
 * next will postMessage to target worker
 */
class WorkerSubject extends Subject {
    worker;
    options;
    constructor(worker, options) {
        super();
        this.worker = worker;
        this.options = options;
        if (typeof Worker !== 'undefined') {
            // Create a new
            this.worker.onmessage = ({ data }) => {
                const parseData = JSON.parse(data);
                super.next(parseData);
            };
        }
    }
    next(value) {
        this.worker.postMessage(JSON.stringify(value));
    }
    start() {
        return defer(() => {
            this.options.onInit();
            return this.asObservable();
        }).pipe(finalize(() => this.options.onDestroy()), share());
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { CdkService, ElementHandler, WorkerSubject, getFormGroupDirtyValues, getIEVersion, getInvalidControls, getScrollbarWidth, markArrayDirty, markControlDirty, markFormDirty, markFormGroupDirtyByKey, markGroupDirty, patchFormGroupFromDirtyValues, resizeObservable, useTakeUntilDestroyed };
